import React, { type ReactNode, useEffect } from 'react';
import { IconButton } from '@atlaskit/button/new';
import { cssMap } from '@atlaskit/css';
import CrossIcon from '@atlaskit/icon/core/cross';
import AddonIcon from '@atlaskit/icon/core/app';
import Image from '@atlaskit/image';
import Modal, { ModalBody, ModalHeader, ModalTitle } from '@atlaskit/modal-dialog';
import { FullScreenModalDialog } from '@atlaskit/modal-dialog/full-screen';
import { Box, Inline, Stack } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { ForgeModalDeprecationMessage } from './ForgeModalDeprecationMessage';
import { type ModalExtension } from './util';
import { getADSModalWidth } from '../utils';
import { resolveModalDimensions } from './forge-ui-renderer/custom-ui-modal-utils';
import { type Extension } from '../web-client';
import { fg } from '@atlaskit/platform-feature-flags';
import { ConfluenceExtension } from '@atlassian/forge-ui-types';
import { useForgeUiAnalyticsEvent } from '../analytics/useForgeUiAnalyticsEvent';
import { getExtensionType } from '../utils/getExtensionType';
import { FULLSCREEN_SUPPORTED_EXTENSION_TYPES } from './forge-ui-renderer/modal/types';
import { isNamedSize, type ModalSizes } from '../custom-ui/bridge/types';

const styles = cssMap({
	modalWrapper: {
		paddingBottom: token('space.300'),
	},
});

const getExtensionIcon = (extension?: Extension): string | undefined => {
	return extension?.properties?.config?.icon ?? extension?.properties?.icon;
};

interface ForgeExtensionPointModalProps {
	children: ReactNode;
	extension?: Extension;
	modalExtension: ModalExtension;
	shouldShowFCFModalDeprecationMessage?: boolean;
}

const ModalWrapper = (props: {
	children: ReactNode;
	closeModalExtension: () => void;
	extensionType?: string;
	modalSize?: ModalSizes;
	shouldCloseOnEscapePress?: boolean;
	shouldCloseOnOverlayClick?: boolean;
}) => {
	const {
		closeModalExtension,
		extensionType,
		modalSize,
		shouldCloseOnOverlayClick,
		shouldCloseOnEscapePress,
	} = props;

	const isFullscreenAllowed =
		extensionType && FULLSCREEN_SUPPORTED_EXTENSION_TYPES.has(extensionType);
	const isFullscreenModal = modalSize === 'fullscreen';

	if (isFullscreenModal && isFullscreenAllowed) {
		return (
			<FullScreenModalDialog
				shouldCloseOnEscapePress={shouldCloseOnEscapePress}
				onClose={closeModalExtension}
			>
				{props.children}
			</FullScreenModalDialog>
		);
	}

	// Fallback to 'x-large' if width is 'fullscreen' but not supported
	const shouldFallbackFromFullscreen = isFullscreenModal && !isFullscreenAllowed;

	const resolvedDimensions = fg('forge-ui-ui-kit-modal-resize-proj')
		? resolveModalDimensions({
				size: modalSize,
				shouldFallbackFromFullscreen,
				isResizableSize: modalSize === 'resizable',
			})
		: undefined;

	// TODO: Delete on forge-ui-ui-kit-modal-resize-proj flag cleanup - we just need resolvedDimensions.
	const namedSize = modalSize !== undefined && isNamedSize(modalSize) ? modalSize : undefined;
	const ADSModalWidth = namedSize ? getADSModalWidth(namedSize) : undefined;
	const widthWithFallback = shouldFallbackFromFullscreen ? 'x-large' : ADSModalWidth;

	const width = resolvedDimensions ? resolvedDimensions.width : widthWithFallback;
	const height = resolvedDimensions?.height;

	if (shouldFallbackFromFullscreen) {
		// eslint-disable-next-line no-console
		console.warn(
			'Forge fullscreen extension point modal is only supported in specific extension points. Refer to https://developer.atlassian.com/platform/forge/manifest-reference/modules/ for more guidance. The modal will be rendered with x-large width instead.',
		);
	}

	return (
		<Modal
			shouldCloseOnOverlayClick={shouldCloseOnOverlayClick}
			shouldCloseOnEscapePress={shouldCloseOnEscapePress}
			onClose={closeModalExtension}
			width={width}
			height={height}
		>
			{props.children}
		</Modal>
	);
};

export const ForgeExtensionPointModal = ({
	children,
	modalExtension,
	shouldShowFCFModalDeprecationMessage,
	extension,
}: ForgeExtensionPointModalProps): React.JSX.Element => {
	const {
		closeModalExtension,
		title,
		modalWidth: modalSize, // TODO: rename this prop on the product side
		shouldCloseOnOverlayClick,
		shouldCloseOnEscapePress,
		viewOnClose,
	} = modalExtension;

	const environmentType = extension?.environmentType;
	const extensionType = extension ? getExtensionType(extension?.properties) : 'extension';
	const isMacroExtension =
		extensionType === 'extension' && extension?.type === ConfluenceExtension.MACRO;
	const isFullscreenModal = modalSize === 'fullscreen';
	const extensionIcon = isMacroExtension ? getExtensionIcon(extension) : undefined;

	const { trackExtensionLoaded, trackBridgeSucceed, trackBridgeFailed } =
		useForgeUiAnalyticsEvent();

	// Only tracking fullscreen modals
	useEffect(() => {
		if (isFullscreenModal) {
			trackExtensionLoaded({
				forgeEnvironment: environmentType,
				renderType: 'UIKit',
				extensionType: extensionType,
				viewportSize: 'fullscreen',
			});
		}
	}, [isFullscreenModal, extensionType, environmentType, trackExtensionLoaded]);

	const handleClose = async () => {
		if (viewOnClose) {
			try {
				await viewOnClose();
				trackBridgeSucceed({
					method: 'onClose',
					forgeEnvironment: extension?.environmentType,
				});
			} catch (error) {
				// Track failure but still close the modal
				trackBridgeFailed({
					method: 'onClose',
					errorName: error instanceof Error ? error.name : 'UnknownError',
					forgeEnvironment: extension?.environmentType,
				});
			} finally {
				closeModalExtension();
			}
		} else {
			closeModalExtension();
		}
	};

	return (
		<ModalWrapper
			shouldCloseOnEscapePress={shouldCloseOnEscapePress}
			closeModalExtension={handleClose}
			extensionType={extension?.type}
			modalSize={modalSize}
			shouldCloseOnOverlayClick={shouldCloseOnOverlayClick}
		>
			<ModalHeader>
				{isMacroExtension && isFullscreenModal ? (
					<Inline space="space.100" alignBlock="center">
						{extensionIcon ? (
							<Image
								src={extensionIcon}
								alt="Macro custom config icon"
								width="24px"
								height="24px"
							/>
						) : (
							<AddonIcon label="" color={token('color.icon.accent.gray')} />
						)}
						<ModalTitle>{title}</ModalTitle>
					</Inline>
				) : (
					<ModalTitle>{title}</ModalTitle>
				)}
				<IconButton
					appearance="subtle"
					icon={CrossIcon}
					label="Close Modal"
					onClick={handleClose}
				/>
			</ModalHeader>
			<ModalBody
				hasInlinePadding={isFullscreenModal && fg('forge-ui-kit-fullscreen-modal') ? false : true}
			>
				{shouldShowFCFModalDeprecationMessage && extension ? (
					<Stack space="space.200">
						<ForgeModalDeprecationMessage extension={extension} />
						<Box xcss={styles.modalWrapper}>{children}</Box>
					</Stack>
				) : (
					<Box xcss={styles.modalWrapper}>{children}</Box>
				)}
			</ModalBody>
		</ModalWrapper>
	);
};
